import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import AddressAutocompleteField from '../../../components/AddressAutocompleteField';

export interface ChangeField {
  key: string;
  label: string;
  initial?: string;
  address?: boolean;
}

interface Props {
  open: boolean;
  title: string;
  description: string;
  fields: ChangeField[];
  warning?: string;
  onClose: () => void;
  onSubmit: (values: Record<string, string>) => void;
}

export default function MfaChangeDialog({
  open,
  title,
  description,
  fields,
  warning,
  onClose,
  onSubmit,
}: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [step, setStep] = useState<'edit' | 'code'>('edit');
  const [code, setCode] = useState('');
  const close = () => {
    setValues({});
    setStep('edit');
    setCode('');
    onClose();
  };
  const filled = fields.every(
    (field) => (values[field.key] ?? field.initial ?? '').trim().length > 0,
  );
  return (
    <Dialog open={open} onClose={close} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent>
        {step === 'edit' ? (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {description}
            </Typography>
            {fields.map((field) => {
              const value = values[field.key] ?? field.initial ?? '';
              const set = (next: string) =>
                setValues((current) => ({ ...current, [field.key]: next }));
              return field.address ? (
                <AddressAutocompleteField
                  key={field.key}
                  label={field.label}
                  value={value}
                  onChange={set}
                />
              ) : (
                <TextField
                  key={field.key}
                  label={field.label}
                  value={value}
                  onChange={(event) => set(event.target.value)}
                />
              );
            })}
            {warning && <Alert severity="warning">{warning}</Alert>}
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Typography variant="body2">
              We sent a 6-digit code to your mobile ending 678. Enter it to confirm this change.
            </Typography>
            <TextField
              label="Verification code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              helperText="Prototype: any 6 digits"
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={close}>Cancel</Button>
        {step === 'edit' ? (
          <Button variant="contained" disabled={!filled} onClick={() => setStep('code')}>
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={!/^\d{6}$/.test(code)}
            onClick={() => {
              const result: Record<string, string> = {};
              for (const field of fields)
                result[field.key] = (values[field.key] ?? field.initial ?? '').trim();
              onSubmit(result);
              close();
            }}
          >
            Confirm
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
